/**
 * Family handover. Big CTA to generate the family-handover PDF.
 * Calls the lazy pdf-lib chain in chassis/handover-pdf.ts.
 */
import { withVault, injectCommonStyles } from './_shared.ts';
import { generateHandoverPDF } from '../chassis/handover-pdf.ts';
import { currentEntitlement, hasFeature } from '../chassis/payment.ts';
import { store } from '../store.ts';
import { makeEsignConnector } from '../chassis/connectors/govt/esign-nsdl.ts';

const TPL = /* html */ `
<style>
.card {
  display: grid;
  gap: 12px;
}
.lede {
  font-family: var(--display);
  font-style: italic;
  font-size: 17px;
  color: var(--ink);
  line-height: 1.45;
}
.gate {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: var(--crimson);
  padding: 8px 12px;
  background: oklch(48% 0.180 25 / 0.06);
  border: 1px solid oklch(48% 0.180 25 / 0.30);
  border-radius: 2px;
}
.status {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--ink-faint);
  margin-top: 4px;
}
.status.ok { color: var(--leaf); }
.status.err { color: var(--crimson); }
</style>
<section class="frame">
  <h2>Family handover <span class="deva">सौंपना</span></h2>
  <div class="kicker">A single PDF your family receives on release</div>
  <div class="card">
    <p class="lede">A step-by-step document that walks your family through every asset, every nominee, every wish. Never contains raw secrets. Always contains exactly what they need.</p>
    <div id="gate-wrap"></div>
    <div class="btn-row">
      <button class="btn" id="gen">Generate PDF</button>
      <button class="btn ghost" id="sign">eSign · NSDL mock</button>
    </div>
    <div class="status" id="status"></div>
  </div>
</section>
`;

export class WasiyatHandoverCard extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub: (() => void) | null = null;
  #lastPdf: Uint8Array | null = null;
  #esign = makeEsignConnector('test');

  connectedCallback(): void {
    injectCommonStyles(this.#shadow);
    this.#shadow.innerHTML += TPL;
    this.#unsub = withVault(this, (_v, root) => {
      const gate = root.getElementById('gate-wrap');
      const ent = currentEntitlement();
      if (gate) {
        gate.innerHTML = hasFeature(ent.tier, 'handover_pdf') ? '' : `<div class="gate">Pro tier required · upgrade below</div>`;
      }
    });
    const btn = this.#shadow.getElementById('gen') as HTMLButtonElement;
    const signBtn = this.#shadow.getElementById('sign') as HTMLButtonElement;
    const status = this.#shadow.getElementById('status') as HTMLDivElement;
    btn.addEventListener('click', async () => {
      const s = store.state;
      if (!s.vault) return;
      const ent = currentEntitlement();
      if (!hasFeature(ent.tier, 'handover_pdf')) {
        status.textContent = 'Pro tier required';
        status.className = 'status err';
        return;
      }
      status.textContent = 'Generating…';
      status.className = 'status';
      try {
        const bytes = await generateHandoverPDF(s.vault);
        if (!bytes) {
          status.textContent = 'pdf-lib failed to load. check your network and try again.';
          status.className = 'status err';
          return;
        }
        this.#lastPdf = bytes;
        const blob = new Blob([bytes as Uint8Array<ArrayBufferLike>], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `wasiyat-handover-${new Date().toISOString().slice(0, 10)}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
        status.textContent = `Generated · ${(bytes.byteLength / 1024).toFixed(1)} KB · ready for eSign`;
        status.className = 'status ok';
      } catch {
        status.textContent = 'Could not generate. Try again.';
        status.className = 'status err';
      }
    });

    signBtn.addEventListener('click', async () => {
      if (!this.#lastPdf) {
        status.textContent = 'Generate the PDF first, then eSign it.';
        status.className = 'status err';
        return;
      }
      const s = store.state;
      const ownerName = s.vault?.owner.display_name ?? 'Vault holder';
      const primaryNominee = s.vault?.nominees.find((n) => n.contact_priority === 1) ?? null;
      this.#esign.prepare({
        pdfBytes: this.#lastPdf,
        signerName: ownerName,
        signerAadhaarLast4: primaryNominee?.aadhaar_last4 ?? '0000',
        purpose: 'will',
        reasonShort: 'Wasiyat family-handover signature',
      });
      status.textContent = 'eSign mock running…';
      status.className = 'status';
      const res = await this.#esign.importPayload();
      if (!res.ok) {
        status.textContent = `eSign failed · ${res.error.message}`;
        status.className = 'status err';
        return;
      }
      const blob = new Blob([res.value.signedPdfBytes as Uint8Array<ArrayBufferLike>], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `wasiyat-handover-${new Date().toISOString().slice(0, 10)}-signed.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      status.textContent = `eSigned · ${res.value.providerCode} · receipt ${res.value.receiptId.slice(0, 10)}…`;
      status.className = 'status ok';
    });
  }
  disconnectedCallback(): void { this.#unsub?.(); }
}

customElements.define('wasiyat-handover-card', WasiyatHandoverCard);
